import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { GeoJSONSource, Map as MlMap, MapMouseEvent } from "maplibre-gl";
import { AttributionControl, LngLatBounds, Map, NavigationControl, ScaleControl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { SearchMarker, type SearchResult } from "../../map/searchMarker";
import { fr } from "../../i18n/fr";
import { buildLayers, SOURCES, type LayerDef } from "../../map/layers";
import { EMPTY, type FC, type LatLonPt } from "../../map/RouteMap";
import { applyBasemap, arrowImage, loadBaseStyle } from "../../map/style";
import { basemapAt, outsideAt, type PackArea } from "../../map/packs";
import type { Styles } from "../../map/symbology";
import { exposeMapForTests } from "../../map/testHooks";
import { featureRestrictionIds } from "../events/geo";

export type DrawMode = "select" | "point" | "path" | "polygon" | "poi" | "hc";
export type Selection = { type: "restriction" | "poi" | "hc"; id: string } | null;

export interface EditMapHandle {
  fit(points: [number, number][]): void;
  flyTo(p: LatLonPt, zoom?: number): void;
  /** marker of a search result with its popup, null removes it (C2) */
  showSearchResult(r: SearchResult | null): void;
  /** ends the current path or polygon drawing (same as a double click) */
  finish(): void;
  cancel(): void;
  undoVertex(): void;
}

interface Props {
  basemapUrl?: string | null;
  symbology: Styles;
  /** what the operator entered: restrictions (lines and points), with properties id, kind, enabled, selected */
  drawn: FC;
  pois: FC;
  /** global height corrections */
  hc: FC;
  overlay: FC;
  zone: FC;
  zoneAdjusted: FC;
  visible: { effects: boolean; drawn: boolean; pois: boolean; hc: boolean; zone: boolean };
  mode: DrawMode;
  /** objects of an import being previewed, drawn in orange while the dialog is open (SW-IM-03) */
  preview?: FC;
  /** object just saved, highlighted for three seconds (SW-RS-12) */
  flash?: FC;
  /** bounding box of the pack of the event [minLon, minLat, maxLon, maxLat]: outside it the basemap is empty (SW-DP-04) */
  bbox?: number[] | null;
  /** packs of the manifest: without [basemapUrl], the basemap of the pack under the view centre is shown */
  packs?: PackArea[];
  onSelect(sel: Selection): void;
  onPoint(p: LatLonPt): void;
  onPath(points: LatLonPt[]): void;
  onPolygon(points: LatLonPt[]): void;
  onDraftChange(count: number): void;
}

const EDIT_SOURCES = ["zone", "zone-adjusted", "edit", "hc", "draft", "preview", "flash"] as const;
const SELECTABLE = ["edit-point", "edit-line", "fx-course", "fx-course-upcoming", "poi", "hc-point", "hc-line"];
const PREVIEW_COLOR = "#e8590c";
const FLASH_COLOR = "#ffd43b";

export const EditMap = forwardRef<EditMapHandle, Props>(function EditMap(props, ref) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const [ready, setReady] = useState(false);
  const layers = useRef<LayerDef[]>([]);
  const pendingFit = useRef<LngLatBounds | null>(null);
  const [outside, setOutside] = useState(false);
  const draft = useRef<LatLonPt[]>([]);
  const search = useRef(new SearchMarker());
  const cb = useRef(props);
  cb.current = props;

  const setDraft = (pts: LatLonPt[]) => {
    draft.current = pts;
    const m = map.current;
    if (!m || !m.getSource("draft")) return;
    const poly = cb.current.mode === "polygon";
    const feats: GeoJSON.Feature[] = pts.map((p) => ({ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: [p.lon, p.lat] } }));
    if (pts.length >= 2) feats.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: [...pts, ...(poly && pts.length >= 3 ? [pts[0]] : [])].map((p) => [p.lon, p.lat]) } });
    if (poly && pts.length >= 3) feats.push({ type: "Feature", properties: { fill: 1 }, geometry: { type: "Polygon", coordinates: [[...pts, pts[0]].map((p) => [p.lon, p.lat])] } });
    (m.getSource("draft") as GeoJSONSource).setData({ type: "FeatureCollection", features: feats });
    cb.current.onDraftChange(pts.length);
  };

  const finish = () => {
    const pts = draft.current;
    const mode = cb.current.mode;
    if (mode === "path" && pts.length >= 2) cb.current.onPath(pts);
    else if (mode === "polygon" && pts.length >= 3) cb.current.onPolygon(pts);
    setDraft([]);
  };

  useImperativeHandle(ref, () => ({
    fit(points) {
      if (points.length === 0) return;
      const b = new LngLatBounds(points[0], points[0]);
      for (const p of points) b.extend(p);
      if (!map.current) { pendingFit.current = b; return; }
      map.current.fitBounds(b, { padding: 60, maxZoom: 17, duration: 400 });
    },
    flyTo(p, zoom = 17) { map.current?.flyTo({ center: [p.lon, p.lat], zoom, duration: 400 }); },
    showSearchResult(r) { if (map.current) search.current.show(map.current, r); },
    finish,
    cancel() { setDraft([]); },
    undoVertex() { setDraft(draft.current.slice(0, -1)); },
  }));

  /** basemap and "outside the packs" notice for the current view and pack (the pack may arrive after the map) */
  const loaded = useRef(false);
  const refresh = (m: MlMap) => {
    const c = m.getCenter();
    setOutside(outsideAt(cb.current.bbox, cb.current.packs, c.lng, c.lat));
    if (loaded.current) applyBasemap(m, basemapAt(cb.current.basemapUrl, cb.current.packs, c.lng, c.lat));
  };

  useEffect(() => {
    let disposed = false;
    loadBaseStyle(cb.current.basemapUrl).then((style) => {
      if (disposed || !container.current) return;
      const m = new Map({
        container: container.current, style, center: [3.523, 50.36], zoom: 13, maxZoom: 19, attributionControl: false, doubleClickZoom: false,
        ...(pendingFit.current ? { bounds: pendingFit.current, fitBoundsOptions: { padding: 60, maxZoom: 17 } } : {}),
      });
      exposeMapForTests(m);
      m.addControl(new AttributionControl({ compact: false, customAttribution: fr.attribution }));
      m.addControl(new NavigationControl({ showCompass: true }), "top-right");
      m.addControl(new ScaleControl({ unit: "metric" }), "bottom-left");
      m.on("load", () => {
        const S = cb.current.symbology;
        m.addImage("arrow-double-green", arrowImage(S.ONEWAY_LIFTED.color, true));
        m.addImage("arrow-violet", arrowImage(S.ONEWAY_REVERSED.color, false));
        m.addImage("arrow-course", arrowImage(S.COURSE.color, false));
        m.addImage("arrow-edit", arrowImage("#0b2e6b", false));
        for (const s of EDIT_SOURCES) m.addSource(s, { type: "geojson", data: EMPTY });
        for (const s of SOURCES) m.addSource(s, { type: "geojson", data: EMPTY });
        // zone under everything
        m.addLayer({ id: "zone-adjusted-line", type: "line", source: "zone-adjusted", paint: { "line-color": "#1d3a8a", "line-width": 1.5, "line-dasharray": [3, 2], "line-opacity": 0.6 } });
        m.addLayer({ id: "zone-fill", type: "fill", source: "zone", paint: { "fill-color": "#1d3a8a", "fill-opacity": 0.06 } });
        m.addLayer({ id: "zone-line", type: "line", source: "zone", paint: { "line-color": "#1d3a8a", "line-width": 2 } });
        layers.current = buildLayers(S).filter((l) => l.group === "effects" || l.group === "barriers" || l.group === "pois");
        for (const l of layers.current) m.addLayer(l.layer);
        m.setPaintProperty("poi", "circle-color", ["coalesce", ["get", "color"], S.POI.color]);
        m.setPaintProperty("poi-label", "text-color", ["coalesce", ["get", "color"], S.POI.color]);
        // height corrections (global)
        m.addLayer({ id: "hc-line", type: "line", source: "hc", filter: ["==", ["geometry-type"], "LineString"], paint: { "line-color": "#495057", "line-width": 5, "line-opacity": 0.8 } });
        m.addLayer({ id: "hc-point", type: "circle", source: "hc", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 8, "circle-color": "#495057", "circle-stroke-color": "#fff", "circle-stroke-width": 2 } });
        m.addLayer({ id: "hc-label", type: "symbol", source: "hc", layout: { "text-field": ["concat", ["number-format", ["get", "maxHeightM"], { locale: "fr-FR", "min-fraction-digits": 2, "max-fraction-digits": 2 }], " m"], "text-font": ["Noto Sans Medium"], "text-size": 11, "text-offset": [0, 1.3], "text-anchor": "top", "text-allow-overlap": true }, paint: { "text-color": "#212529", "text-halo-color": "#fff", "text-halo-width": 2 } });
        // what the operator entered, coloured by kind
        const kindColor: unknown = ["match", ["get", "kind"],
          "ONEWAY_LIFTED", S.ONEWAY_LIFTED.color, "ONEWAY_REVERSED", S.ONEWAY_REVERSED.color, "EMERGENCY_ONLY", S.EMERGENCY_ACCESS.color, "COURSE", S.COURSE.color,
          "HEIGHT_LIMIT", S.HEIGHT_OVERRIDE.color, ["case", ["==", ["get", "crossable"], true], S.BARRIER_CROSSABLE.color, S.BARRIER_NON_CROSSABLE.color]];
        const dim: unknown = ["case", ["==", ["get", "enabled"], false], 0.35, 1];
        m.addLayer({ id: "edit-sel-line", type: "line", source: "edit", filter: ["all", ["==", ["geometry-type"], "LineString"], ["==", ["get", "selected"], true], ["!=", ["get", "original"], true]], layout: { "line-cap": "round" }, paint: { "line-color": "#ffd43b", "line-width": 12, "line-opacity": 0.9 } });
        // a course is drawn snapped on the roads; its drawn line (`original`) only shows, thin, while it is redrawn (1.1.1)
        const notOriginal = ["!=", ["get", "original"], true];
        m.addLayer({ id: "edit-line", type: "line", source: "edit", filter: ["all", ["==", ["geometry-type"], "LineString"], notOriginal] as never, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": kindColor as never, "line-width": 5, "line-opacity": dim as never } });
        m.addLayer({ id: "edit-line-original", type: "line", source: "edit", filter: ["all", ["==", ["geometry-type"], "LineString"], ["==", ["get", "original"], true]], paint: { "line-color": S.DRAWN.color, "line-width": 2, "line-dasharray": [2, 1.5] } });
        // the drawing order is a direction for a reversed oneway (new direction) and a course (direction of the runners)
        m.addLayer({ id: "edit-line-arrows", type: "symbol", source: "edit", filter: ["all", ["==", ["geometry-type"], "LineString"], notOriginal, ["in", ["get", "kind"], ["literal", ["ONEWAY_REVERSED", "COURSE"]]]] as never,
          layout: { "symbol-placement": "line", "symbol-spacing": 50, "icon-image": ["match", ["get", "kind"], "COURSE", "arrow-course", "arrow-violet"], "icon-allow-overlap": true } });
        m.addLayer({ id: "edit-sel-point", type: "circle", source: "edit", filter: ["all", ["==", ["geometry-type"], "Point"], ["==", ["get", "selected"], true]], paint: { "circle-radius": 14, "circle-color": "#ffd43b", "circle-opacity": 0.9 } });
        m.addLayer({ id: "edit-point", type: "circle", source: "edit", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 8, "circle-color": kindColor as never, "circle-stroke-color": "#fff", "circle-stroke-width": 2, "circle-opacity": dim as never } });
        m.addLayer({ id: "edit-point-label", type: "symbol", source: "edit", filter: ["==", ["geometry-type"], "Point"], layout: { "text-field": ["case", ["==", ["get", "kind"], "HEIGHT_LIMIT"], "h", ["==", ["get", "crossable"], true], "+", "×"], "text-font": ["Noto Sans Medium"], "text-size": 12, "text-allow-overlap": true }, paint: { "text-color": "#fff" } });
        m.addLayer({ id: "poi-sel", type: "circle", source: "pois", filter: ["==", ["get", "selected"], true], paint: { "circle-radius": 14, "circle-color": "#ffd43b", "circle-opacity": 0.9 } }, "poi");
        // drawing in progress
        m.addLayer({ id: "draft-fill", type: "fill", source: "draft", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": "#1d3a8a", "fill-opacity": 0.12 } });
        m.addLayer({ id: "draft-line", type: "line", source: "draft", filter: ["==", ["geometry-type"], "LineString"], paint: { "line-color": "#0b2e6b", "line-width": 3, "line-dasharray": [2, 1.5] } });
        m.addLayer({ id: "draft-point", type: "circle", source: "draft", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 5, "circle-color": "#fff", "circle-stroke-color": "#0b2e6b", "circle-stroke-width": 2 } });
        // import preview (SW-IM-03)
        m.addLayer({ id: "preview-line", type: "line", source: "preview", filter: ["==", ["geometry-type"], "LineString"], layout: { "line-cap": "round" }, paint: { "line-color": PREVIEW_COLOR, "line-width": 4, "line-dasharray": [1.5, 1] } });
        m.addLayer({ id: "preview-point", type: "circle", source: "preview", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 7, "circle-color": PREVIEW_COLOR, "circle-stroke-color": "#fff", "circle-stroke-width": 2 } });
        m.addLayer({ id: "preview-label", type: "symbol", source: "preview", layout: { "text-field": ["get", "name"], "text-font": ["Noto Sans Medium"], "text-size": 11, "text-offset": [0, 1.2], "text-anchor": "top", "text-optional": true }, paint: { "text-color": PREVIEW_COLOR, "text-halo-color": "#fff", "text-halo-width": 2 } });
        // saved object highlight (SW-RS-12)
        m.addLayer({ id: "flash-line", type: "line", source: "flash", filter: ["==", ["geometry-type"], "LineString"], layout: { "line-cap": "round" }, paint: { "line-color": FLASH_COLOR, "line-width": 16, "line-opacity": 0.8 } });
        m.addLayer({ id: "flash-point", type: "circle", source: "flash", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": 18, "circle-color": FLASH_COLOR, "circle-opacity": 0.8 } });
        loaded.current = true;
        refresh(m);
        setReady(true);
      });
      // outside the pack the basemap has no tiles: grid and notice (SW-DP-04)
      m.on("moveend", () => refresh(m));
      m.on("click", (e: MapMouseEvent) => {
        const p = { lat: e.lngLat.lat, lon: e.lngLat.lng };
        const mode = cb.current.mode;
        if (mode === "point" || mode === "poi" || mode === "hc") { cb.current.onPoint(p); return; }
        if (mode === "path" || mode === "polygon") { setDraft([...draft.current, p]); return; }
        const hits = m.queryRenderedFeatures(e.point, { layers: SELECTABLE.filter((id) => m.getLayer(id)) });
        if (hits.length === 0) { cb.current.onSelect(null); return; }
        const h = hits[0];
        // a course is selected on its snapped stretches (network effects, 1.1.1)
        const id = h.layer.id.startsWith("fx-") ? featureRestrictionIds(h.properties)[0] ?? "" : String(h.properties?.id ?? "");
        if (!id) return;
        cb.current.onSelect({ type: h.layer.id === "poi" ? "poi" : h.layer.id.startsWith("hc") ? "hc" : "restriction", id });
      });
      m.on("dblclick", (e) => { e.preventDefault(); if (cb.current.mode === "path" || cb.current.mode === "polygon") { if (draft.current.length > 0 && cb.current.mode === "path") setDraft(draft.current.slice(0, -1)); finish(); } });
      for (const id of SELECTABLE) {
        m.on("mouseenter", id, () => { if (cb.current.mode === "select") m.getCanvas().style.cursor = "pointer"; });
        m.on("mouseleave", id, () => { if (cb.current.mode === "select") m.getCanvas().style.cursor = ""; });
      }
      map.current = m;
    });
    return () => { disposed = true; loaded.current = false; map.current?.remove(); map.current = null; };
  }, []);

  // pack known or changed after the map was created: basemap and notice follow (event without zone or object, admin page)
  useEffect(() => {
    if (ready && map.current) refresh(map.current);
  }, [ready, props.basemapUrl, props.bbox, props.packs]); // eslint-disable-line react-hooks/exhaustive-deps

  // cursor by mode, drawing reset when the mode changes
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    m.getCanvas().style.cursor = props.mode === "select" ? "" : "crosshair";
    setDraft([]);
  }, [props.mode, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ready || !map.current) return;
    const m = map.current;
    (m.getSource("edit") as GeoJSONSource).setData(props.drawn);
    (m.getSource("pois") as GeoJSONSource).setData(props.pois);
    (m.getSource("hc") as GeoJSONSource).setData(props.hc);
    (m.getSource("overlay") as GeoJSONSource).setData(props.overlay);
    (m.getSource("zone") as GeoJSONSource).setData(props.zone);
    (m.getSource("zone-adjusted") as GeoJSONSource).setData(props.zoneAdjusted);
  }, [ready, props.drawn, props.pois, props.hc, props.overlay, props.zone, props.zoneAdjusted]);

  useEffect(() => {
    if (!ready || !map.current) return;
    (map.current.getSource("preview") as GeoJSONSource).setData(props.preview ?? EMPTY);
  }, [ready, props.preview]);

  // the saved object blinks for three seconds then the highlight goes away
  useEffect(() => {
    if (!ready || !map.current) return;
    const m = map.current;
    const src = m.getSource("flash") as GeoJSONSource;
    if (!props.flash || props.flash.features.length === 0) { src.setData(EMPTY); return; }
    src.setData(props.flash);
    let on = true;
    const blink = setInterval(() => {
      on = !on;
      for (const [id, prop] of [["flash-line", "line-opacity"], ["flash-point", "circle-opacity"]] as const) if (m.getLayer(id)) m.setPaintProperty(id, prop, on ? 0.8 : 0.15);
    }, 400);
    const end = setTimeout(() => { clearInterval(blink); src.setData(EMPTY); }, 3000);
    return () => { clearInterval(blink); clearTimeout(end); src.setData(EMPTY); };
  }, [ready, props.flash]);

  useEffect(() => {
    if (!ready || !map.current) return;
    const m = map.current;
    const vis = (ids: string[], on: boolean) => ids.forEach((id) => m.getLayer(id) && m.setLayoutProperty(id, "visibility", on ? "visible" : "none"));
    vis(layers.current.filter((l) => l.group === "effects" || l.group === "barriers").map((l) => l.layer.id), props.visible.effects);
    vis(["edit-sel-line", "edit-line", "edit-line-original", "edit-line-arrows", "edit-sel-point", "edit-point", "edit-point-label"], props.visible.drawn);
    vis(["poi", "poi-label", "poi-sel"], props.visible.pois);
    vis(["hc-line", "hc-point", "hc-label"], props.visible.hc);
    vis(["zone-fill", "zone-line", "zone-adjusted-line"], props.visible.zone);
  }, [ready, props.visible]);

  return (
    <div className={"map-wrap" + (outside ? " outside" : "")}>
      <div ref={container} className="map" />
      {outside && <div className="map-notice">{fr.workspace.outsidePacks}</div>}
    </div>
  );
});
